import { gql } from '@apollo/client'

import { AppType } from '../../../_shared/types/app-types'
import { TCustomer } from './mutations'

/**
 * The return type from the GET_APP_API_KEY query
 */
export type AppAPIKeyReturnType = {
  appAPIKey: { apiKey: string | null; appType: AppType; enabled: boolean }
}

/**
 * Get APP's API Key
 */

export const GET_APP_API_KEY = gql`
  query AppAPIKey($appType: String!) {
    appAPIKey(input: { appType: $appType }) {
      appType
      apiKey
      enabled
    }
  }
`

/**
 * Get a workspace's apps. (Currently backend only returns enabled)
 */
export const GET_WORKSPACE_APPS = gql`
  query {
    workspaceApps {
      workspaceID
      appType
      enabledByUserID
      enabled
      externalWorkspaceID
      appMetadata {
        companyID
        encodedCredentialsValidated
        rateConfirmationEmail
        allowedEmailAddresses
        customers {
          customerID
          customerNameInChaine
          customerIDInChaine
          integrationConfigurationID
        }
        mcLeodInstanceURL
        mcLeodVersion
        mcLeodServerTimezone
        onboardingURL
        tmsURL
      }
      appCredentials {
        apiKey
        chaineAPIToken
        password
        username
      }
    }
  }
`
/**
 * The return type from workspaceApps (GET_WORKSPACE_APPS) query
 */
export type WorkspaceAppsReturnType = {
  workspaceApps: WorkspaceApp[]
}

/**
 * A workspace app with non-sensitive info
 */
export interface WorkspaceApp {
  /**
   * App credentials, it can be API Key, Token, Client ID or Client Secret etc.
   */
  appCredentials?: {
    apiKey?: string
    chaineAPIToken?: string
    password?: string
    username?: string
  }
  appMetadata?: {
    allowedEmailAddresses?: string[]

    /**
     * The company ID from the McLeod app
     */
    companyID?: string
    /**
     * When app is FourKites it will have customers array for which the app is enabled by Broker inside the workspace
     */
    customers?: TCustomer[]

    /**
     * Some apps may have a domain name associated with them.
     * i.e. For RMIS, https://<domainname>.rmissecure.com/_c/std/api/
     */
    domainName?: string

    /**
     * This is true if the user has provided validated credentials. We use this to know what step on the web-client app to show the user when they are enabling a project44 app.
     */
    encodedCredentialsValidated?: boolean

    /**
     * The instance URL which is part of the McLeod app
     */
    mcLeodInstanceURL?: string

    /**
     * McLeod's customer server timezone, which is used to calculate the time offset in stop and location APIs
     */
    mcLeodServerTimezone?: string

    /**
     * The McLeod version
     */
    mcLeodVersion?: string

    /**
     * Carrier vetting apps may have provided a specific onboarding URL for the workspace's enabled app
     */
    onboardingURL?: string
    rateConfirmationEmail?: string

    /**
     * The URL of a tms if it is needed
     */
    tmsURL?: string
  }
  /**
   * The app of {@link AppType}
   */
  appType: AppType
  /**
   * Indicates if the app enabled
   */
  enabled: boolean

  /**
   * The user that enabled the app
   */
  enabledByUserID: string

  /**
   * For apps that generate an external workspace ID, this is the external workspace ID
   */
  externalWorkspaceID?: string
  /**
   * The workspaceID of the app
   */
  workspaceID: string
}
